import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Form, FieldArray, Field } from 'formik';
import { TERMS_AND_CONDITIONS_TEMPLATES } from '../common/termsAndConditionsTemplates';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, Collapse, Divider, FormControl, Grid, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Stack, Switch, TextField, Typography } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import DropzoneArea from '../../common/DropzoneArea';
import { useGetQuotationsUploadProgressQuery, useGetStakeholdersByTypeByTypeQuery, usePostQuotationsUploadProgressMutation } from '../../../redux/vmsApi';
import { useGetProcurementContractsQuery, useLazyGetContractItemsByContractQuery } from '../../../redux/procurementContractsApi';

import { Delete as DeleteIcon } from '@mui/icons-material';
import { formatCurrency } from "../../../utils/currencyUtil";
import useDebounce from '../../../hooks/useDebounce';
import { roundToTwoDecimals } from 'utils/formattingUtil';
import { calculateTotalWithDiscount } from 'utils/discountUtil';
import { getVatRate } from '../../../utils/vatUtil';
import { getStakeholderStatusBadges } from '../../../utils/stakeholderStatusUtil';
import { lazyWithReload } from '../../../utils/lazyWithReload';

const VAT_RATE = getVatRate(new Date());
type QuotationSource = 'pcReview' | 'savedProgress' | 'requisitionItems';

const ProcurementCalculator = lazyWithReload(() => import('../common/procurementCalculator'), 'reloaded-procurementCalculator-chunk');
const CompetitiveAnalysis = lazyWithReload(() => import('../common/competetiveAnalysis'), 'reloaded-competetiveAnalysis-chunk');



const calculateQuotationSummary = (quotation: any) => {
  const totalVAT = quotation.quotationItems && Array.isArray(quotation.quotationItems)
    ? quotation.quotationItems.reduce((acc: number, item: any) => {
      const VAT = item.VAT ? VAT_RATE * (item.rate - (item.discountPerItem || 0)) * item.quantity.quantityValue : 0;
      return acc + VAT;
    }, 0)
    : 0;

  const totalWithDiscount = quotation.quotationItems
    ? calculateTotalWithDiscount(quotation.quotationItems, quotation.discount || 0)
    : 0;
  const totalb4VAT = totalWithDiscount - totalVAT;

  const totalDiscount = quotation.quotationItems && Array.isArray(quotation.quotationItems)
    ? quotation.quotationItems.reduce((acc: number, item: any) => {
      const itemDiscount = (item.discountPerItem || 0) * (item.quantity.quantityValue || 0);
      return acc + itemDiscount;
    }, 0)
    : 0;

  return {
    totalVAT: roundToTwoDecimals(totalVAT),
    totalb4VAT: roundToTwoDecimals(totalb4VAT),
    grandTotal: roundToTwoDecimals(totalWithDiscount),
    totalDiscount: roundToTwoDecimals(totalDiscount),
  };
};

const createQuotationItemsFromRequisition = (items: any[] = []) => items.map((item: any) => ({
  description: item.description,
  itemCode: item.itemCode,
  quantity: {
    quantityValue: item.quantity?.quantityValue || '',
    quantityUnit: item.quantity?.quantityUnit || '',
  },
  rate: '',
  totalCostPerItem: (item.quantity?.quantityValue || 0) * (item.rate || 0),
  VAT: false,
  procured: item.procured || false,
  unit: item.unit || { id: '', name: '' },
  discountPerItem: '',
}));

const getDefaultQuotationSource = (pcReviewQuotations: any[] = [], savedProgressQuotations: any[] = []) => {
  if (pcReviewQuotations.length > 0) {
    return 'pcReview';
  }

  if (savedProgressQuotations.length > 0) {
    return 'savedProgress';
  }

  return 'requisitionItems';
};

const QuotationItemRow = React.memo(({
  item,
  qIndex,
  iIndex,
  quotationDiscount,
  quotationCurrency,
  contractItemRule,
  setFieldValue,
  removeItem,
}: any) => {
  const fieldBase = `quotations.${qIndex}.quotationItems.${iIndex}`;

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`${fieldBase}.description`, e.target.value, false);
  }, [fieldBase, setFieldValue]);

  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const quantityValue = parseFloat(e.target.value) || 0;
    const baseCost = (item.rate - item.discountPerItem) * quantityValue;
    const VAT = item.VAT ? VAT_RATE * (item.rate - item.discountPerItem) * quantityValue : 0;
    const totalCost = baseCost + VAT;

    setFieldValue(`${fieldBase}.quantity.quantityValue`, e.target.value, false);
    setFieldValue(`${fieldBase}.totalCostPerItem`, roundToTwoDecimals(totalCost), false);
  }, [fieldBase, item.rate, item.discountPerItem, item.VAT, setFieldValue]);

  const handleQuantityUnitChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`${fieldBase}.quantity.quantityUnit`, e.target.value, false);
  }, [fieldBase, setFieldValue]);

  const handleRateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rate = parseFloat(e.target.value) || 0;
    const baseCost = (rate - item.discountPerItem) * item.quantity.quantityValue;
    const VAT = item.VAT ? VAT_RATE * (item.rate - item.discountPerItem) * item.quantity.quantityValue : 0;
    const totalCost = baseCost + VAT;

    setFieldValue(`${fieldBase}.rate`, rate, false);
    setFieldValue(`${fieldBase}.totalCostPerItem`, totalCost, false);
  }, [fieldBase, item.discountPerItem, item.quantity.quantityValue, item.VAT, item.rate, setFieldValue]);

  const handleDiscountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const discountPerItem = parseFloat(e.target.value) || 0;
    const baseCost = (item.rate - discountPerItem) * item.quantity.quantityValue;
    const VAT = item.VAT ? VAT_RATE * (item.rate - item.discountPerItem) : 0;
    const totalCost = baseCost + VAT;

    setFieldValue(`${fieldBase}.discountPerItem`, discountPerItem, false);
    setFieldValue(`${fieldBase}.totalCostPerItem`, roundToTwoDecimals(totalCost), false);

    if (discountPerItem > 0) {
      setFieldValue(`quotations.${qIndex}.discount`, 0, false);
    }
  }, [fieldBase, item.rate, item.discountPerItem, item.quantity.quantityValue, item.VAT, qIndex, setFieldValue]);

  const handleVatChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const VATEnabled = e.target.checked;
    const baseCost = (item.rate - item.discountPerItem) * item.quantity.quantityValue;
    const VAT = VATEnabled ? VAT_RATE * (item.rate - item.discountPerItem) * item.quantity.quantityValue : 0;
    const totalCost = baseCost + VAT;

    setFieldValue(`${fieldBase}.VAT`, VATEnabled, false);
    setFieldValue(`${fieldBase}.totalCostPerItem`, roundToTwoDecimals(totalCost), false);
  }, [fieldBase, item.rate, item.discountPerItem, item.quantity.quantityValue, setFieldValue]);

  const handleRemoveItem = useCallback(() => {
    removeItem(iIndex);
  }, [removeItem, iIndex]);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        name={`${fieldBase}.description`}
        label="Description"
        fullWidth
        value={item.description}
        onChange={handleDescriptionChange}
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
      />
      <TextField
        name={`${fieldBase}.quantity.quantityValue`}
        label="Quantity"
        type="number"
        value={item.quantity.quantityValue}
        onChange={handleQuantityChange}
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
      />
      <TextField
        name={`${fieldBase}.quantity.quantityUnit`}
        InputProps={{
          readOnly: true,
        }}
        value={item.quantity.quantityUnit || ''}
        onChange={handleQuantityUnitChange}
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
      />
      <TextField
        name={`${fieldBase}.rate`}
        label="Rate"
        type="number"
        value={item.rate === '' ? '' : Number(item.rate)}
        onChange={handleRateChange}
        disabled={contractItemRule?.hasBudgetedCost}
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
      />
      <TextField
        name={`${fieldBase}.discountPerItem`}
        label="Discount per Item"
        type="number"
        value={item.discountPerItem}
        onChange={handleDiscountChange}
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
        disabled={quotationDiscount > 0}
      />
      <Switch
        checked={item.VAT || false}
        onChange={handleVatChange}
        disabled={typeof contractItemRule?.isVAT === 'boolean'}
      />
      <Typography>VAT: {item.VAT ? "Yes (15.5%)" : "No"}</Typography>

      <TextField
        name={`${fieldBase}.totalCostPerItem`}
        label="Total Amount"
        value={formatCurrency(item.totalCostPerItem || 0, quotationCurrency || 'usd')}
        disabled
        sx={{ backgroundColor: '#fff', borderRadius: 1 }}
      />

      <IconButton color="error" onClick={handleRemoveItem}><DeleteIcon /></IconButton>
    </Stack>
  );
}, (prevProps, nextProps) => {
  return prevProps.item === nextProps.item
    && prevProps.qIndex === nextProps.qIndex
    && prevProps.iIndex === nextProps.iIndex
    && prevProps.quotationDiscount === nextProps.quotationDiscount
    && prevProps.quotationCurrency === nextProps.quotationCurrency
    && prevProps.contractItemRule === nextProps.contractItemRule;
});

const QuotationCard = React.memo(({
  quotation,
  qIndex,
  stakeholders,
  contractItemRules,
  setFieldValue,
  removeQuotation,
  handleSupplierChange,
  isCalculatorOpen,
  toggleCalculator,
  isExpanded,
  toggleExpanded,
}: any) => {
  const handleRemoveQuotation = useCallback(() => {
    removeQuotation(qIndex);
  }, [removeQuotation, qIndex]);

  const handleQuotationNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`quotations.${qIndex}.quotationNumber`, e.target.value, false);
  }, [qIndex, setFieldValue]);

  const handleRemarksChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`quotations.${qIndex}.remarks`, e.target.value, false);
  }, [qIndex, setFieldValue]);

  const handleUpfrontPercentageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setFieldValue(`quotations.${qIndex}.paymentUpfrontPercentage`, '', false);
      return;
    }
    const parsed = Math.min(100, Math.max(0, Math.trunc(Number(raw)) || 0));
    setFieldValue(`quotations.${qIndex}.paymentUpfrontPercentage`, parsed, false);
  }, [qIndex, setFieldValue]);

  const handleLeadTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`quotations.${qIndex}.deliveryLeadTime`, e.target.value, false);
  }, [qIndex, setFieldValue]);

  const handleTermsTemplateChange = useCallback((e: SelectChangeEvent<any>) => {
    const template = TERMS_AND_CONDITIONS_TEMPLATES.find((t) => t.key === e.target.value);
    setFieldValue(`quotations.${qIndex}.termsAndConditionsTemplate`, e.target.value, false);
    setFieldValue(`quotations.${qIndex}.termsAndConditions`, template?.text ?? '', false);
  }, [qIndex, setFieldValue]);

  const handleTermsTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`quotations.${qIndex}.termsAndConditions`, e.target.value, false);
  }, [qIndex, setFieldValue]);

  const handleCurrencyChange = useCallback((e: SelectChangeEvent<any>) => {
    setFieldValue(`quotations.${qIndex}.currency`, e.target.value, false);
  }, [qIndex, setFieldValue]);

  const handleToggleCalculator = useCallback(() => {
    toggleCalculator(qIndex);
  }, [toggleCalculator, qIndex]);

  const handleToggleExpanded = useCallback(() => {
    toggleExpanded(qIndex);
  }, [toggleExpanded, qIndex]);

  return (
    <Card sx={{ margin: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 4, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', width: '100%' }}>
      <CardHeader
        title={<Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>Supplier({qIndex + 1})</Typography>}
        action={<Stack direction="row" spacing={1} alignItems="center"><Button size="small" variant="outlined" onClick={handleToggleExpanded}>{isExpanded ? 'Hide Items' : 'Show Items'}</Button><IconButton onClick={handleRemoveQuotation} color="error"><RemoveCircleOutline /></IconButton></Stack>}
      />
      <Divider sx={{ marginY: 2 }} />
      <CardContent>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" gutterBottom>Supplier</Typography>
            <Select
              name={`quotations.${qIndex}.supplier`}
              fullWidth
              value={quotation.supplier?.id || ''}
              onChange={(e) => handleSupplierChange(e, qIndex)}
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            >
              {stakeholders?.map((stakeholder: any) => (
                <MenuItem
                  key={stakeholder.id}
                  value={stakeholder.id}
                  disabled={!stakeholder.isStakeholderVerified}
                >
                  <Stack spacing={0.5} sx={{ width: '100%' }}>
                    <Typography variant="body2">
                      {stakeholder.name}
                      {!stakeholder.isStakeholderVerified && ' (Unverified)'}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {getStakeholderStatusBadges(stakeholder).map((badge) => (
                        <Chip
                          key={badge.label}
                          label={badge.label}
                          color={badge.color as any}
                          variant={badge.variant}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>

            {quotation.supplier?.id && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {getStakeholderStatusBadges(
                  stakeholders?.find((stakeholder: any) => stakeholder.id === quotation.supplier.id)
                ).map((badge) => (
                  <Chip
                    key={badge.label}
                    label={badge.label}
                    color={badge.color as any}
                    variant={badge.variant}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                ))}
              </Stack>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
              Payment Terms
            </Typography>
            <TextField
              name={`quotations.${qIndex}.paymentTerms`}
              fullWidth
              variant="outlined"
              value={quotation.paymentTerms}
              InputProps={{
                readOnly: true,
              }}
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
              Quotation Number
            </Typography>
            <TextField
              name={`quotations.${qIndex}.quotationNumber`}
              fullWidth
              variant="outlined"
              value={quotation.quotationNumber}
              onChange={handleQuotationNumberChange}
              placeholder="Enter Quotation Number"
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            />
          </Grid>

          <Field name={`quotations.${qIndex}.totalVAT`} type="hidden" />
          <Field name={`quotations.${qIndex}.totalDiscount`} type="hidden" />
          <Field name={`quotations.${qIndex}.totalb4VAT`} type="hidden" />
          <Field name={`quotations.${qIndex}.grandTotal`} type="hidden" />
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Remarks
              </Typography>
              <TextField
                name={`quotations.${qIndex}.remarks`}
                fullWidth
                variant="outlined"
                value={quotation.remarks}
                onChange={handleRemarksChange}
                placeholder="Enter remarks"
                sx={{ backgroundColor: '#fff', borderRadius: 1 }}
              />
            </Grid>

            <Grid item xs={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Upfront (%) *
              </Typography>
              <TextField
                name={`quotations.${qIndex}.paymentUpfrontPercentage`}
                required
                error={quotation.paymentUpfrontPercentage === '' || quotation.paymentUpfrontPercentage == null}
                type="number"
                fullWidth
                variant="outlined"
                value={quotation.paymentUpfrontPercentage ?? ''}
                onChange={handleUpfrontPercentageChange}
                placeholder="0 - 100"
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ backgroundColor: '#fff', borderRadius: 1 }}
              />
            </Grid>

            <Grid item xs={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Lead Time (Days) *
              </Typography>
              <TextField
                name={`quotations.${qIndex}.deliveryLeadTime`}
                required
                error={quotation.deliveryLeadTime === '' || quotation.deliveryLeadTime == null || Number(quotation.deliveryLeadTime) <= 0}
                type="number"
                fullWidth
                variant="outlined"
                value={quotation.deliveryLeadTime}
                onChange={handleLeadTimeChange}
                placeholder="Delivery Lead Time(Days)"
                sx={{ backgroundColor: '#fff', borderRadius: 1 }}
              />
            </Grid>

            <Grid item xs={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Currency
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ backgroundColor: '#fff', borderRadius: 1 }}>
                <Select
                  name={`quotations.${qIndex}.currency`}
                  value={quotation.currency || 'USD'}
                  onChange={handleCurrencyChange}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZIG">ZIG</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
                Terms &amp; Conditions / SLA
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ backgroundColor: '#fff', borderRadius: 1, mb: 1 }}>
                <Select
                  displayEmpty
                  name={`quotations.${qIndex}.termsAndConditionsTemplate`}
                  value={quotation.termsAndConditionsTemplate || ''}
                  onChange={handleTermsTemplateChange}
                  renderValue={(selected: any) => TERMS_AND_CONDITIONS_TEMPLATES.find((t) => t.key === selected)?.label || 'Select a template (optional)'}
                >
                  {TERMS_AND_CONDITIONS_TEMPLATES.map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                name={`quotations.${qIndex}.termsAndConditions`}
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={quotation.termsAndConditions ?? ''}
                onChange={handleTermsTextChange}
                placeholder="Select a template above, then edit the details here"
                sx={{ backgroundColor: '#fff', borderRadius: 1 }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
              Attachments
            </Typography>
            <DropzoneArea
              setFieldValue={setFieldValue}
              fieldName={`quotations.${qIndex}.attachments`}
              attachments={quotation.attachments || []}
            />
          </Grid>
          <br></br>
          <div>
            <Button
              variant="contained"
              onClick={handleToggleCalculator}
              sx={{
                marginBottom: 2,
                backgroundColor: "gold",
                color: "black",
                "&:hover": {
                  backgroundColor: "yellow",
                },
              }}
            >
              {isCalculatorOpen ? "Hide Procurement Calculator" : "Use Procurement Calculator"}
            </Button>

            {isCalculatorOpen && (
              <Suspense fallback={<Typography variant="body2">Loading calculator...</Typography>}>
                <div>
                  <ProcurementCalculator />
                </div>
              </Suspense>
            )}
          </div>
          <br></br>

          <FieldArray name={`quotations.${qIndex}.quotationItems`}>
            {({ push: pushItem, remove: removeItem }) => (
              <Stack spacing={2} mt={3}>
                {quotation.quotationItems && Array.isArray(quotation.quotationItems) && quotation.quotationItems.map((item: any, iIndex: number) => (
                  <QuotationItemRow
                    key={iIndex}
                    item={item}
                    qIndex={qIndex}
                    iIndex={iIndex}
                    quotationDiscount={quotation.discount}
                    quotationCurrency={quotation.currency}
                    contractItemRule={contractItemRules?.[`${qIndex}.${iIndex}`]}
                    setFieldValue={setFieldValue}
                    removeItem={removeItem}
                  />
                ))}
                <Button
                  onClick={() => pushItem({ description: '', quantity: { quantityValue: 0, quantityUnit: '' }, rate: 0, totalCostPerItem: 0, VAT: false, unit: { id: '', name: '' }, procured: false })}
                  variant="contained"
                  size="small"
                >
                  Add Item
                </Button>
              </Stack>
            )}
          </FieldArray>
        </Collapse>
      </CardContent>
      <Card sx={{ padding: '15px', backgroundColor: '#d1e7dd', textAlign: 'right', borderRadius: 2 }}>
        <Stack direction="column" spacing={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 'bold' }}>
              Total (Excl. VAT):
            </Typography>
            <Typography variant="subtitle2" sx={{ color: '#333' }}>
              {formatCurrency(quotation.totalb4VAT || 0, quotation.currency || 'usd')}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 'bold' }}>
              VAT(15.5%):
            </Typography>
            <Typography variant="subtitle2" sx={{ color: '#333' }}>
              {formatCurrency(quotation.totalVAT || 0, quotation.currency || 'usd')}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 'bold' }}>
              Grand Total :
            </Typography>
            <Typography variant="subtitle2" sx={{ color: '#333' }}>
              {formatCurrency(quotation.grandTotal || 0, quotation.currency || 'usd')}
            </Typography>
          </Box>
        </Stack>
      </Card>
    </Card>
  );
}, (prevProps, nextProps) => {
  return prevProps.quotation === nextProps.quotation
    && prevProps.qIndex === nextProps.qIndex
    && prevProps.isCalculatorOpen === nextProps.isCalculatorOpen
    && prevProps.isExpanded === nextProps.isExpanded
    && prevProps.stakeholders === nextProps.stakeholders
    && prevProps.contractItemRules === nextProps.contractItemRules;
});

const QuotationForm: React.FC<any> = ({handleSubmit, setFieldValue, values, showQuotationForm, setShowQuotationForm, procurementProcess }) => {
  const [activeCalculatorIndex, setActiveCalculatorIndex] = useState<number | null>(null);
  const [expandedQuotationIndex, setExpandedQuotationIndex] = useState<number>(0);
  const [contractItemRules, setContractItemRules] = useState<Record<string, { hasBudgetedCost: boolean; isVAT?: boolean }>>({});
  const pcReviewQuotations = Array.isArray(procurementProcess?.pcReview?.quotations)
    ? procurementProcess.pcReview.quotations
    : [];
  const requisitionItems = Array.isArray(procurementProcess?.requisition?.items)
    ? procurementProcess.requisition.items
    : [];

  const toggleCalculator = useCallback((index: number) => {
    setActiveCalculatorIndex((prev) => (prev === index ? null : index));
  }, []);

  const toggleExpandedQuotation = useCallback((index: number) => {
    setExpandedQuotationIndex((prev) => (prev === index ? -1 : index));
  }, []);

  const [reqNumber, setReqNumber] = useState(procurementProcess?.requisition?.reqNumber || '');

  const { data: savedProgress, refetch } = useGetQuotationsUploadProgressQuery(
    { reqNumber: reqNumber }
  );

  const [saveQuotations] = usePostQuotationsUploadProgressMutation();
  const [feedback, setFeedback] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [quotationSource, setQuotationSource] = useState<QuotationSource>(getDefaultQuotationSource(pcReviewQuotations));
  const [hasInitializedQuotations, setHasInitializedQuotations] = useState(false);

  const savedProgressQuotations = Array.isArray(savedProgress) && savedProgress.length > 0 && Array.isArray(savedProgress[0]?.quotations)
    ? savedProgress[0].quotations
    : [];

  const applyQuotationSource = useCallback((source: QuotationSource) => {
    let nextQuotations = values.quotations;

    if (source === 'pcReview' && pcReviewQuotations.length > 0) {
      nextQuotations = pcReviewQuotations;
    } else if (source === 'savedProgress' && savedProgressQuotations.length > 0) {
      nextQuotations = savedProgressQuotations;
    } else if (source === 'requisitionItems' && requisitionItems.length > 0) {
      nextQuotations = values.quotations.map((quotation: any) => ({
        ...quotation,
        quotationItems: createQuotationItemsFromRequisition(requisitionItems),
      }));
    }

    setFieldValue('quotations', nextQuotations, false);
    setQuotationSource(source);
    setHasInitializedQuotations(true);
  }, [pcReviewQuotations, requisitionItems, savedProgressQuotations, setFieldValue, values.quotations]);

  const handleFetchQuotations = useCallback(async () => {
    const result = await refetch();
    const fetchedSavedProgressQuotations = Array.isArray(result.data)
      && result.data.length > 0
      && Array.isArray(result.data[0]?.quotations)
      ? result.data[0].quotations
      : [];

    if (quotationSource === 'savedProgress' && fetchedSavedProgressQuotations.length > 0) {
      setFieldValue('quotations', fetchedSavedProgressQuotations, false);
      setHasInitializedQuotations(true);
    }
  }, [quotationSource, refetch, setFieldValue]);

  useEffect(() => {
    if (reqNumber) {
      refetch();
    }
  }, [reqNumber, refetch]);

  // Show all suppliers, but only allow verified suppliers to be selected.
  const { data: stakeholders } = useGetStakeholdersByTypeByTypeQuery({ type: 'Supplier' });
  const { data: procurementContracts = [] } = useGetProcurementContractsQuery();
  const [fetchContractItemsByContract] = useLazyGetContractItemsByContractQuery();

  const handleApproval = async (isApproved: boolean) => {
    if (!values.comment && !showQuotationForm) {
      toast.error("Please provide a comment before proceeding.", {
        position: 'top-center',
        theme: 'colored',
      });
      return;
    } else if (showQuotationForm && (values.quotations[0]?.attachments?.length === 0 || values.quotations.length === 0)) {
      toast.error("Please provide at least one quotation.", {
        position: 'top-center',
        theme: 'colored',
      });
      return;
    } else if (showQuotationForm && values.quotations.some((q: any) => q?.paymentUpfrontPercentage === '' || q?.paymentUpfrontPercentage == null)) {
      toast.error("Please enter the Upfront (%) for every quotation.", {
        position: 'top-center',
        theme: 'colored',
      });
      return;
    } else if (showQuotationForm && values.quotations.some((q: any) => q?.deliveryLeadTime === '' || q?.deliveryLeadTime == null || Number(q?.deliveryLeadTime) <= 0)) {
      toast.error("Please enter a Lead Time (Days) greater than 0 for every quotation.", {
        position: 'top-center',
        theme: 'colored',
      });
      return;
    } else {
      values.approved = isApproved;
      handleSubmit(values);
    }
  };

  const handleSupplierChange = useCallback(async (event: SelectChangeEvent<any>, index: number) => {
    const supplierId = event.target.value;
    const supplierObject = stakeholders?.find((supplier: any) => supplier.id === supplierId);

    setContractItemRules((previousRules) => Object.fromEntries(
      Object.entries(previousRules).filter(([key]) => !key.startsWith(`${index}.`)),
    ));
    setFieldValue(`quotations.${index}.supplier`, { id: supplierId, name: supplierObject?.name || '' }, false);
    setFieldValue(`quotations.${index}.paymentTerms`, supplierObject?.paymentTerms || '', false);

    if (!supplierId) {
      return;
    }

    // Auto-fill rate/VAT for items that match an active contract's items for this supplier.
    console.log('[ContractAutoFill] supplier selected', { supplierId, procurementContractsCount: procurementContracts.length });

    const activeSupplierContracts = procurementContracts.filter(
      (contract) => contract.active && String(contract.supplier?.id) === String(supplierId) && contract.id,
    );

    console.log('[ContractAutoFill] active contracts for supplier', activeSupplierContracts);

    if (activeSupplierContracts.length === 0) {
      return;
    }

    try {
      const contractItemLists = await Promise.all(
        activeSupplierContracts.map((contract) =>
          fetchContractItemsByContract({ contractId: contract.id as string, limit: 1000, skip: 0 })
            .unwrap()
            .catch((fetchError) => {
              console.error('[ContractAutoFill] failed to fetch contract items', { contractId: contract.id, fetchError });
              return [];
            }),
        ),
      );
      const allContractItems = contractItemLists.flat();

      console.log('[ContractAutoFill] fetched contract items', allContractItems);

      if (allContractItems.length === 0) {
        return;
      }

      const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase();
      const currentItems = values.quotations?.[index]?.quotationItems;

      console.log('[ContractAutoFill] current quotation items', currentItems);

      if (!Array.isArray(currentItems)) {
        return;
      }

      const matchedItemRules: Record<string, { hasBudgetedCost: boolean; isVAT?: boolean }> = {};
      currentItems.forEach((item: any, iIndex: number) => {
        const matchedContractItem = allContractItems.find(
          (contractItem) =>
            normalize(contractItem.itemCode) === normalize(item.itemCode)
            && normalize(contractItem.itemDescription) === normalize(item.description),
        );

        console.log('[ContractAutoFill] matching item', {
          iIndex,
          itemCode: item.itemCode,
          description: item.description,
          matchedContractItem,
        });

        if (!matchedContractItem) {
          return;
        }

        const vatEnabled = Boolean(matchedContractItem.isVAT);
        const hasBudgetedCost = matchedContractItem.itemBudgetedCost !== undefined
          && matchedContractItem.itemBudgetedCost !== null;
        const contractRate = Number(matchedContractItem.itemBudgetedCost) || 0;
        const rate = vatEnabled
          ? contractRate / (1 + VAT_RATE)
          : contractRate;
        const discountPerItem = Number(item.discountPerItem) || 0;
        const quantityValue = Number(item.quantity?.quantityValue) || 0;
        const baseCost = (rate - discountPerItem) * quantityValue;
        const vatAmount = vatEnabled ? VAT_RATE * (rate - discountPerItem) * quantityValue : 0;

        const itemFieldBase = `quotations.${index}.quotationItems.${iIndex}`;
        matchedItemRules[`${index}.${iIndex}`] = {
          hasBudgetedCost,
          isVAT: typeof matchedContractItem.isVAT === 'boolean' ? matchedContractItem.isVAT : undefined,
        };
        console.log('[ContractAutoFill] applying rate/VAT', { itemFieldBase, rate, vatEnabled, totalCost: roundToTwoDecimals(baseCost + vatAmount) });
        setFieldValue(`${itemFieldBase}.rate`, rate, false);
        setFieldValue(`${itemFieldBase}.VAT`, vatEnabled, false);
        setFieldValue(`${itemFieldBase}.totalCostPerItem`, roundToTwoDecimals(baseCost + vatAmount), false);
      });
      setContractItemRules((previousRules) => ({ ...previousRules, ...matchedItemRules }));
    } catch (error) {
      console.error('[ContractAutoFill] Failed to auto-fill rates from contract items:', error);
    }
  }, [setFieldValue, stakeholders, procurementContracts, fetchContractItemsByContract, values.quotations]);

  const debouncedQuotations = useDebounce(values.quotations, 300);
  useEffect(() => {
    const updatedQuotations = values.quotations.map((quotation: any) => {
      const summary = calculateQuotationSummary(quotation);
      const hasSummaryChanges = quotation.totalVAT !== summary.totalVAT
        || quotation.totalb4VAT !== summary.totalb4VAT
        || quotation.grandTotal !== summary.grandTotal
        || quotation.totalDiscount !== summary.totalDiscount;

      return hasSummaryChanges ? { ...quotation, ...summary } : quotation;
    });

    const hasChanges = updatedQuotations.some((quotation: any, index: number) => quotation !== values.quotations[index]);

    if (hasChanges) {
      setFieldValue('quotations', updatedQuotations, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuotations, setFieldValue]);

  useEffect(() => {
    const defaultSource = getDefaultQuotationSource(pcReviewQuotations, savedProgressQuotations);

    if (!hasInitializedQuotations) {
      applyQuotationSource(defaultSource);
      return;
    }

    if (quotationSource === 'pcReview' && pcReviewQuotations.length === 0) {
      setQuotationSource(defaultSource);
    }

    if (quotationSource === 'savedProgress' && savedProgressQuotations.length === 0) {
      setQuotationSource(defaultSource);
    }
  }, [applyQuotationSource, hasInitializedQuotations, pcReviewQuotations, quotationSource, savedProgressQuotations]);

  const handleSaveQuotations = async () => {
    if (!reqNumber) {
      setFeedback({ message: "Enter a requisition number before saving.", type: "error" });
      return;
    }

    const payload = {
      reqNumber,
      quotations: values.quotations,
    };

    try {
      await saveQuotations({ newQuotationsUploadProgress: payload }).unwrap();

      setFeedback({ message: "Quotations progress saved successfully!", type: "success" });
      refetch();
    } catch (error: any) {
      const backendMessage = error?.data?.message || error?.error;
      setFeedback({
        message: backendMessage ? `Error saving quotations progress: ${backendMessage}` : "Error saving quotations progress.",
        type: "error",
      });
    }
  };

  return (

    <Form>

      {showQuotationForm && (
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, maxWidth: "80%", mx: "auto" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Retrieve or Save Quotations
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Enter Req Number"
              variant="outlined"
              fullWidth
              value={reqNumber}
              onChange={(e) => {
                setReqNumber(e.target.value)

              }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleFetchQuotations}
              disabled={!reqNumber}
            >
              Fetch Quotations
            </Button>

            <FormControl fullWidth>
              <Select
                value={quotationSource}
                onChange={(event) => setQuotationSource(event.target.value as QuotationSource)}
              >
                <MenuItem value="pcReview" disabled={pcReviewQuotations.length === 0}>
                  Use procurement review quotations
                </MenuItem>
                <MenuItem value="savedProgress" disabled={savedProgressQuotations.length === 0}>
                  Use saved quotations progress
                </MenuItem>
                <MenuItem value="requisitionItems" disabled={requisitionItems.length === 0}>
                  Use requisition items template
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              type="button"
              onClick={() => applyQuotationSource(quotationSource)}
              disabled={
                (quotationSource === 'pcReview' && pcReviewQuotations.length === 0)
                || (quotationSource === 'savedProgress' && savedProgressQuotations.length === 0)
                || (quotationSource === 'requisitionItems' && requisitionItems.length === 0)
              }
            >
              Apply Selected Source
            </Button>

            {Array.isArray(savedProgress) && savedProgress.length > 0 ? (
              <Alert severity="success">Quotations progress retrieved.</Alert>
            ) : (
              <Alert severity="warning">No saved progress found.</Alert>
            )}

            {pcReviewQuotations.length > 0 && (
              <Alert severity="info">Procurement review quotations are available.</Alert>
            )}

            {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

            <Button
              variant="contained"
              type="button"
              color="success"
              fullWidth
              onClick={handleSaveQuotations}
            >
              Save Quotations
            </Button>
          </Stack>
        </Paper>
      )}
      <Stack spacing={4} width="100%" p={10} pb={5}>
        {!showQuotationForm && (
          <>

            <TextField
              name="comment"
              label="Comments"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              onChange={(e) => setFieldValue("comment", e.target.value)}
            />


            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <ConfirmationDialog
                  actionType="Approve"

                  onConfirm={() => handleApproval(true)}
                />
                <ConfirmationDialog
                  actionType="Reject"

                  onConfirm={() => handleApproval(false)}
                />
              </Stack>
            </Stack>
          </>
        )}        {showQuotationForm && (
          <FieldArray name="quotations">
            {({ push: pushQuotation, remove: removeQuotation }) => (
              <Stack spacing={3}>
                {values.quotations.map((quotation: any, qIndex: number) => (
                  <QuotationCard
                    key={qIndex}
                    quotation={quotation}
                    qIndex={qIndex}
                    stakeholders={stakeholders}
                    contractItemRules={contractItemRules}
                    setFieldValue={setFieldValue}
                    removeQuotation={removeQuotation}
                    handleSupplierChange={handleSupplierChange}
                    isCalculatorOpen={activeCalculatorIndex === qIndex}
                    toggleCalculator={toggleCalculator}
                    isExpanded={expandedQuotationIndex === qIndex}
                    toggleExpanded={toggleExpandedQuotation}
                  />
                ))}
                <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={() => pushQuotation({ supplier: '', paymentTerms: '', remarks: '', paymentUpfrontPercentage: '', deliveryLeadTime: '', termsAndConditionsTemplate: '', termsAndConditions: '', currency: '', quotationNumber: '', attachments: [], quotationItems: createQuotationItemsFromRequisition(requisitionItems) })}>Add Quotation</Button>
              </Stack>
            )}

          </FieldArray>

        )}

        {showQuotationForm && (
          <Suspense fallback={<Typography variant="body2">Loading analysis...</Typography>}>
            <CompetitiveAnalysis quotations={values.quotations} setFieldValue={setFieldValue} />
          </Suspense>
        )}
        <ConfirmationDialog
          actionType="Submit Info"
          disabled={!values.quotations?.length || values.quotations.some((q: any) => !q?.supplier?.id || q?.paymentUpfrontPercentage === '' || q?.paymentUpfrontPercentage == null || q?.deliveryLeadTime === '' || q?.deliveryLeadTime == null || Number(q?.deliveryLeadTime) <= 0)}

          onConfirm={() => handleApproval(true)}
        />
        <Button color="primary" variant="outlined" onClick={() => setShowQuotationForm(false)}>Go back Dashboard</Button>

      </Stack>
    </Form>


  );
};

export default QuotationForm;
